/**
 * The colour system, and the one rule that makes it safe.
 *
 * Two things live here and they are deliberately in the same file, because the
 * second is meaningless without the first:
 *
 *  1. `STATE_COLOURS` — one colour per monitor state. Five states, five hues,
 *     no greys and no shared tokens (consumer obligations §2, §10 rule 3).
 *  2. `constrainAccent` — REQ-503's guarantee that a per-organisation brand
 *     accent can never render as one of those five.
 *
 * ## Why the accent has to be constrained rather than trusted
 *
 * REQ-503 makes the client's logo and accent colour configurable per
 * organisation, and consumer obligations §4 spells out the consequence: "a
 * client whose brand colour is green must not produce a page where green stops
 * meaning `up`." An accent is arbitrary data supplied by someone who has never
 * read this file. If it is rendered as given, the safety property of the state
 * palette is only as good as the taste of whoever typed the hex code.
 *
 * So the accent is not validated and rejected — rejection would leave the page
 * with no accent, and someone would eventually "fix" that by dropping the
 * check. It is *transformed*: the hue is rotated out of every state's exclusion
 * band and into the nearest safe one. The caller always gets a usable colour,
 * and it is never confusable with a state.
 *
 * ## Colour is never the only carrier
 *
 * Consumer obligations §8 records that no accessibility level is specified
 * anywhere, and flags this exact model as the reason that gap matters: colour
 * alone cannot be the carrier of "this monitor is not being checked". Every
 * state therefore also has a `glyph` and a text label, and the UI renders all
 * three together. The colour is the fast path, not the channel.
 */
import type { MonitorState } from "../api/index.ts";
import { MONITOR_STATES } from "../api/index.ts";

/** How a single state is drawn. */
export interface StateColour {
  /** The chip's foreground/border hue. A saturated hex, never a grey. */
  readonly hex: string;
  /** Chip background — the same hue, heavily tinted. */
  readonly tint: string;
  /**
   * A shape that reads without colour. Five distinct glyphs, so a monochrome
   * rendering, a colour-blind reader and a black-and-white printout all keep
   * the five states apart.
   */
  readonly glyph: string;
}

/**
 * Five hues, one per state.
 *
 * Chosen far apart on the wheel so no two states are a near-miss, and none of
 * them is a neutral: `disabled`, `never_run` and `stale` each get their own
 * saturated colour rather than sharing a grey dot, which §2 calls out as
 * failing REQ-105 as surely as a green one would.
 */
export const STATE_COLOURS: Readonly<Record<MonitorState, StateColour>> = {
  // ~137° green. The only colour in this table that means health.
  up: { hex: "#1a7f37", tint: "#e4f4e9", glyph: "●" },
  // ~356° red.
  down: { hex: "#c1121f", tint: "#fbe6e7", glyph: "▲" },
  // ~32° amber. A state in its own right, not a "warning tier" between two
  // others — §2 forbids inventing that, and this is not one.
  stale: { hex: "#b45309", tint: "#fdefe0", glyph: "◐" },
  // ~201° blue.
  never_run: { hex: "#0369a1", tint: "#e0f0f9", glyph: "○" },
  // ~275° violet.
  disabled: { hex: "#6b21a8", tint: "#f1e6fa", glyph: "◼" },
};

/** The state hues, in degrees, derived from the table above rather than retyped. */
const STATE_HUES: readonly number[] = MONITOR_STATES.map((state) => hueOf(STATE_COLOURS[state].hex));

/**
 * How far an accent's hue must stay from every state hue.
 *
 * 30° is wide enough that a rotated accent is visibly a different colour rather
 * than an off-shade of the state next to it, and narrow enough that five bands
 * of 60° still leave usable gaps to land in.
 */
export const ACCENT_HUE_MARGIN_DEGREES = 30;

/**
 * Below this saturation a colour has no hue a person can name — it reads as a
 * grey or a near-black, and cannot be mistaken for a state chip. Those pass
 * through untouched, because rotating the hue of a grey changes nothing anyway.
 */
export const ACCENT_NEUTRAL_SATURATION = 0.12;

/**
 * The accent used when none is configured, and when a supplied one is
 * unparseable.
 *
 * A near-neutral slate, and deliberately so: it is below
 * `ACCENT_NEUTRAL_SATURATION`, so it is safe by the same rule every other
 * accent is judged by rather than by being hand-picked and trusted. The test
 * suite asserts `isAccentSafe(DEFAULT_ACCENT)`, which is what stops a future
 * edit quietly making the fallback the one colour that breaks the guarantee.
 */
export const DEFAULT_ACCENT = "#3f434a";

export interface ConstrainedAccent {
  /** The colour to render. Always safe against all five state colours. */
  readonly hex: string;
  /** True when the supplied colour had to be moved. */
  readonly adjusted: boolean;
  /** Why, in one line — for a settings screen to show back to whoever set it. */
  readonly reason: string;
}

interface Hsl {
  readonly h: number;
  readonly s: number;
  readonly l: number;
}

function parseHex(value: string): { r: number; g: number; b: number } | null {
  const raw = value.trim().replace(/^#/, "");
  const expanded =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw;

  if (!/^[0-9a-fA-F]{6}$/.test(expanded)) return null;

  return {
    r: Number.parseInt(expanded.slice(0, 2), 16),
    g: Number.parseInt(expanded.slice(2, 4), 16),
    b: Number.parseInt(expanded.slice(4, 6), 16),
  };
}

function rgbToHsl(r: number, g: number, b: number): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  const l = (max + min) / 2;

  if (delta === 0) return { h: 0, s: 0, l };

  const s = delta / (1 - Math.abs(2 * l - 1));

  let h: number;
  if (max === rn) h = 60 * (((gn - bn) / delta) % 6);
  else if (max === gn) h = 60 * ((bn - rn) / delta + 2);
  else h = 60 * ((rn - gn) / delta + 4);

  return { h: (h + 360) % 360, s, l };
}

function hslToHex({ h, s, l }: Hsl): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  const [r1, g1, b1] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];

  const channel = (v: number): string =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");

  return `#${channel(r1 ?? 0)}${channel(g1 ?? 0)}${channel(b1 ?? 0)}`;
}

function hueOf(hex: string): number {
  const rgb = parseHex(hex);
  // Every value in STATE_COLOURS is a literal in this file, so this cannot fail
  // in practice; returning 0 keeps the function total rather than throwing at
  // module load.
  if (rgb === null) return 0;
  return rgbToHsl(rgb.r, rgb.g, rgb.b).h;
}

/** Shortest distance between two hues on the 360° wheel. */
export function hueDistance(a: number, b: number): number {
  const raw = Math.abs(((a % 360) + 360) % 360 - (((b % 360) + 360) % 360));
  return raw > 180 ? 360 - raw : raw;
}

/** True when this hue sits inside any state's exclusion band. */
function collides(hue: number): boolean {
  return STATE_HUES.some((stateHue) => hueDistance(hue, stateHue) < ACCENT_HUE_MARGIN_DEGREES);
}

/**
 * The margin `nearestSafeHue` actually aims for.
 *
 * A degree wider than the band, because the chosen hue is round-tripped through
 * an 8-bit-per-channel hex before anyone checks it again. Landing exactly on the
 * boundary and then rounding inwards would produce a colour this module had just
 * declared safe and `isAccentSafe` would reject — a contradiction that is much
 * easier to prevent here than to debug later.
 */
const SAFE_HUE_TARGET_DEGREES = ACCENT_HUE_MARGIN_DEGREES + 1;

function clearsEveryBand(hue: number): boolean {
  return STATE_HUES.every((stateHue) => hueDistance(hue, stateHue) >= SAFE_HUE_TARGET_DEGREES);
}

/**
 * The nearest hue that is outside every band.
 *
 * Walks outward from the requested hue a degree at a time in both directions,
 * so the rotation is the smallest one that works and the result still resembles
 * what was asked for. A full sweep that finds nothing means the bands cover the
 * wheel; the caller falls back to the neutral default rather than picking one
 * at random.
 */
function nearestSafeHue(hue: number): number | null {
  for (let offset = 0; offset <= 180; offset += 1) {
    const up = (hue + offset) % 360;
    if (clearsEveryBand(up)) return up;
    const down = (hue - offset + 360) % 360;
    if (clearsEveryBand(down)) return down;
  }
  return null;
}

/**
 * REQ-503: make a per-organisation accent safe to render next to state colours.
 *
 * Total by construction — every input yields a usable colour:
 *  - unparseable, or absent → `DEFAULT_ACCENT`
 *  - near-neutral → returned as supplied (it has no nameable hue to confuse)
 *  - outside all five bands → returned as supplied
 *  - inside a band → hue rotated to the nearest safe one, saturation and
 *    lightness untouched so it still reads as the brand's weight
 */
export function constrainAccent(supplied: string | null | undefined): ConstrainedAccent {
  if (supplied === null || supplied === undefined || supplied.trim() === "") {
    return { hex: DEFAULT_ACCENT, adjusted: false, reason: "No accent configured; using the default." };
  }

  const rgb = parseHex(supplied);
  if (rgb === null) {
    return {
      hex: DEFAULT_ACCENT,
      adjusted: true,
      reason: `"${supplied}" is not a hex colour; using the default.`,
    };
  }

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  if (hsl.s < ACCENT_NEUTRAL_SATURATION) {
    return { hex: hslToHex(hsl), adjusted: false, reason: "Near-neutral; no state colour to collide with." };
  }

  if (!collides(hsl.h)) {
    return { hex: hslToHex(hsl), adjusted: false, reason: "Clear of every state colour." };
  }

  const safeHue = nearestSafeHue(hsl.h);
  if (safeHue === null) {
    return {
      hex: DEFAULT_ACCENT,
      adjusted: true,
      reason: "No hue on the wheel is clear of the five state colours; using the default.",
    };
  }

  return {
    hex: hslToHex({ h: safeHue, s: hsl.s, l: hsl.l }),
    adjusted: true,
    reason: `Rotated ${Math.round(hueDistance(hsl.h, safeHue))}° away from a monitor state colour (REQ-503).`,
  };
}

/**
 * Whether a colour is safe to render alongside the state palette.
 *
 * Exposed so a test — and, later, a settings screen — can assert the property
 * directly rather than trusting that `constrainAccent` did its job.
 */
export function isAccentSafe(hex: string): boolean {
  const rgb = parseHex(hex);
  if (rgb === null) return false;
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  if (hsl.s < ACCENT_NEUTRAL_SATURATION) return true;
  return !collides(hsl.h);
}
